import { useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';

import { PortalSettingsDTO } from '../hooks/portal';

import styles from './ChangeActiveSettingModal.module.scss';

interface ChangeActiveSettingModalProps {
    open: boolean;
    onClose: () => void;
    settings: PortalSettingsDTO[];
    onSetActive: (id: string) => Promise<void>;
    isLoading: boolean;
}

export const ChangeActiveSettingModal = ({
    open,
    onClose,
    settings,
    onSetActive,
    isLoading,
}: ChangeActiveSettingModalProps) => {
    const [selectedSetting, setSelectedSetting] =
        useState<PortalSettingsDTO | null>(null);

    const activeSetting = settings.find((s) => s.active);

    const handleSubmit = async () => {
        if (!selectedSetting) return;

        try {
            await onSetActive(selectedSetting.id);
            handleClose();
        } catch (error) {
            console.error('Failed to set active setting:', error);
            alert('Failed to set active setting');
        }
    };

    const handleSetNoneActive = async () => {
        try {
            // Send an invalid UUID to deactivate all settings
            await onSetActive('00000000-0000-0000-0000-000000000000');
            handleClose();
        } catch (error) {
            console.error('Failed to deactivate settings:', error);
            alert('Failed to deactivate settings');
        }
    };

    const handleClose = () => {
        setSelectedSetting(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Change Active Setting</DialogTitle>
            <DialogContent>
                <div className={styles.content}>
                    {activeSetting && (
                        <div className={styles.currentActive}>
                            <span className={styles.label}>
                                Current Active:
                            </span>
                            <span className={styles.value}>
                                {activeSetting.name}
                            </span>
                        </div>
                    )}

                    <Autocomplete
                        options={settings}
                        getOptionLabel={(option) => option.name}
                        value={selectedSetting}
                        onChange={(_, newValue) => setSelectedSetting(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Setting"
                                placeholder="Type to search..."
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <div className={styles.menuItem}>
                                    <span>{option.name}</span>
                                    {option.active && (
                                        <span className={styles.activeBadge}>
                                            Active
                                        </span>
                                    )}
                                </div>
                            </li>
                        )}
                        fullWidth
                        className={styles.autocomplete}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSetNoneActive}
                    variant="outlined"
                    color="warning"
                    disabled={isLoading}
                >
                    Set None Active
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedSetting || isLoading}
                >
                    {isLoading ? 'Setting...' : 'Set Active'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
